import React, { useState, useEffect } from 'react';
import { webhooksAPI } from '../../services/api';
import { useUser } from '../../context/UserContext';
import '../admin/AdminComponents.css';

const WebhooksManagement = () => {
  const { hasPermission } = useUser();
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'orders',
    url: '',
    active: true,
    mentions: []
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await webhooksAPI.getAll();
      if (response.success) {
        setWebhooks(response.data || []);
      } else {
        setError(response.message || 'Failed to load webhooks');
      }
    } catch (err) {
      console.error('Error loading webhooks:', err);
      setError(err.message || 'Failed to load webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (webhook) => {
    setEditingWebhook(webhook);
    let mentions = [];
    if (Array.isArray(webhook.mentions)) {
      mentions = webhook.mentions.map(m => {
        if (typeof m === 'string') {
          return { type: 'user', id: m };
        }
        return m;
      });
    }
    setFormData({
      type: webhook.type,
      url: webhook.url,
      active: webhook.active,
      mentions: mentions
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this webhook?')) {
      return;
    }

    try {
      const response = await webhooksAPI.delete(id);
      if (response.success) {
        await loadWebhooks();
      } else {
        alert(response.message || 'Failed to delete webhook');
      }
    } catch (err) {
      console.error('Error deleting webhook:', err);
      alert(err.message || 'Failed to delete webhook');
    }
  };

  const handleToggleActive = async (webhook) => {
    try {
      const response = await webhooksAPI.update(webhook.id, {
        active: !webhook.active
      });
      if (response.success) {
        await loadWebhooks();
      } else {
        alert(response.message || 'Failed to update webhook');
      }
    } catch (err) {
      console.error('Error updating webhook:', err);
      alert(err.message || 'Failed to update webhook');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      if (editingWebhook) {
        const response = await webhooksAPI.update(editingWebhook.id, {
          url: formData.url,
          active: formData.active,
          mentions: Array.isArray(formData.mentions) ? formData.mentions.filter(m => {
            if (typeof m === 'string') return m && m.trim();
            return m && m.id && m.type && (m.type === 'user' || m.type === 'role');
          }) : []
        });
        if (response.success) {
          await loadWebhooks();
          handleFormClose();
        } else {
          setError(response.message || 'Failed to update webhook');
        }
      } else {
        const webhookData = {
          ...formData,
          active: typeof formData.active === 'boolean' ? formData.active : formData.active === true || formData.active === 'true',
          mentions: Array.isArray(formData.mentions) ? formData.mentions.filter(m => {
            if (typeof m === 'string') return m && m.trim();
            return m && m.id && m.type && (m.type === 'user' || m.type === 'role');
          }) : []
        };
        const response = await webhooksAPI.create(webhookData);
        if (response.success) {
          await loadWebhooks();
          handleFormClose();
        } else {
          if (response.errors && Array.isArray(response.errors)) {
            const errorMessages = response.errors.map(e => e.msg || e.message).join(', ');
            setError(response.message + ': ' + errorMessages);
          } else {
            setError(response.message || 'Failed to create webhook');
          }
        }
      }
    } catch (err) {
      console.error('Error saving webhook:', err);
      const errorData = err.response?.data || {};
      if (errorData.errors && Array.isArray(errorData.errors)) {
        const errorMessages = errorData.errors.map(e => e.msg || e.message).join(', ');
        setError((errorData.message || 'Validation failed') + ': ' + errorMessages);
      } else {
        setError(err.message || 'Failed to save webhook');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingWebhook(null);
    setFormData({
      type: 'orders',
      url: '',
      active: true,
      mentions: []
    });
    setError('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (typeof value === 'string' ? value.trim() : value)
    }));
  };

  if (loading) {
    return (
      <div className="admin-section">
        <p>Loading webhooks...</p>
      </div>
    );
  }
  const ordersWebhooks = webhooks.filter(w => w.type === 'orders');
  const newApplicationsWebhooks = webhooks.filter(w => w.type === 'new_applications');
  const applicationStatusWebhooks = webhooks.filter(w => w.type === 'application_status');
  return (
    <div className="admin-section">
      <div className="admin-section-header">
        <h2 className="admin-section-title">Webhooks Management</h2>
        <div className="admin-section-actions">
          <button
            className="admin-button primary"
            onClick={() => {
              setEditingWebhook(null);
              setFormData({
                type: 'orders',
                url: '',
                active: true,
                mentions: []
              });
              setShowForm(true);
            }}
          >
            Add Webhook
          </button>
        </div>
      </div>

      {error && !showForm && (
        <div className="error-message" style={{ marginBottom: '1rem', padding: '1rem' }}>
          {error}
        </div>
      )}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Orders Webhooks</h3>
        {ordersWebhooks.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No webhooks configured for orders</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Mentions</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ordersWebhooks.map((webhook) => (
                  <tr key={webhook.id}>
                    <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {webhook.url}
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      {webhook.mentions && Array.isArray(webhook.mentions) && webhook.mentions.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {webhook.mentions.map((mention, idx) => {
                            const mentionObj = typeof mention === 'string' ? { type: 'user', id: mention } : mention;
                            return (
                              <span key={idx} style={{
                                padding: '0.25rem 0.5rem',
                                background: mentionObj.type === 'role' ? 'rgba(255, 107, 107, 0.2)' : 'var(--bg-tertiary, #2a2a2a)',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                color: 'var(--text-secondary)',
                                border: mentionObj.type === 'role' ? '1px solid var(--accent-color, #ff6b6b)' : 'none'
                              }}>
                                {mentionObj.type === 'role' ? '👥 ' : '👤 '}{mentionObj.id}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>None</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="admin-button"
                        style={{
                          backgroundColor: webhook.active ? '#32cd32' : '#666',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.85rem',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleActive(webhook)}
                        title={webhook.active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {webhook.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>{new Date(webhook.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="admin-icon-button"
                          onClick={() => handleEdit(webhook)}
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          className="admin-icon-button danger"
                          onClick={() => handleDelete(webhook.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>New Applications Webhooks</h3>
        {newApplicationsWebhooks.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No webhooks configured for new applications</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Mentions</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {newApplicationsWebhooks.map((webhook) => (
                  <tr key={webhook.id}>
                    <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {webhook.url}
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      {webhook.mentions && Array.isArray(webhook.mentions) && webhook.mentions.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {webhook.mentions.map((mention, idx) => {
                            const mentionObj = typeof mention === 'string' ? { type: 'user', id: mention } : mention;
                            return (
                              <span key={idx} style={{
                                padding: '0.25rem 0.5rem',
                                background: mentionObj.type === 'role' ? 'rgba(255, 107, 107, 0.2)' : 'var(--bg-tertiary, #2a2a2a)',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                color: 'var(--text-secondary)',
                                border: mentionObj.type === 'role' ? '1px solid var(--accent-color, #ff6b6b)' : 'none'
                              }}>
                                {mentionObj.type === 'role' ? '👥 ' : '👤 '}{mentionObj.id}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>None</span>

                      )}
                    </td>
                    <td>
                      <button
                        className="admin-button"
                        style={{
                          backgroundColor: webhook.active ? '#32cd32' : '#666',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.85rem',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleActive(webhook)}
                        title={webhook.active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {webhook.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>{new Date(webhook.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="admin-icon-button"
                          onClick={() => handleEdit(webhook)}
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          className="admin-icon-button danger"
                          onClick={() => handleDelete(webhook.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div>
        <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>Application Status Change Webhooks</h3>
        {applicationStatusWebhooks.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No webhooks configured for application status changes</p>
        ) : (
          <div className="admin-table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Mentions</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {applicationStatusWebhooks.map((webhook) => (
                  <tr key={webhook.id}>
                    <td style={{ maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {webhook.url}
                    </td>
                    <td style={{ maxWidth: '200px' }}>
                      {webhook.mentions && Array.isArray(webhook.mentions) && webhook.mentions.length > 0 ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                          {webhook.mentions.map((mention, idx) => {
                            const mentionObj = typeof mention === 'string' ? { type: 'user', id: mention } : mention;
                            return (
                              <span key={idx} style={{
                                padding: '0.25rem 0.5rem',
                                background: mentionObj.type === 'role' ? 'rgba(255, 107, 107, 0.2)' : 'var(--bg-tertiary, #2a2a2a)',
                                borderRadius: '4px',
                                fontSize: '0.85rem',
                                color: 'var(--text-secondary)',
                                border: mentionObj.type === 'role' ? '1px solid var(--accent-color, #ff6b6b)' : 'none'
                              }}>
                                {mentionObj.type === 'role' ? '👥 ' : '👤 '}{mentionObj.id}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>None</span>
                      )}
                    </td>
                    <td>
                      <button
                        className="admin-button"
                        style={{
                          backgroundColor: webhook.active ? '#32cd32' : '#666',
                          color: 'white',
                          padding: '0.25rem 0.75rem',
                          fontSize: '0.85rem',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleToggleActive(webhook)}
                        title={webhook.active ? 'Click to deactivate' : 'Click to activate'}
                      >
                        {webhook.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td>{new Date(webhook.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-table-actions">
                        <button
                          className="admin-icon-button"
                          onClick={() => handleEdit(webhook)}
                          title="Edit"
                        >
                          ✎
                        </button>
                        <button
                          className="admin-icon-button danger"
                          onClick={() => handleDelete(webhook.id)}
                          title="Delete"
                        >
                          ×
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showForm && (
        <div className="admin-modal-overlay" onClick={handleFormClose}>
          <div className="admin-modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="admin-modal-header">
              <h3>{editingWebhook ? 'Edit Webhook' : 'Add Webhook'}</h3>
              <button className="admin-modal-close" onClick={handleFormClose}>×</button>
            </div>
            <form className="admin-form" style={{ padding: '1.5rem' }} onSubmit={handleFormSubmit}>
              {error && (
                <div className="error-message" style={{ marginBottom: '1rem', padding: '0.75rem' }}>
                  {error}
                </div>
              )}
              <div className="admin-form-group">
                <label className="admin-form-label">Type</label>
                <select
                  className="admin-form-select"
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  disabled={!!editingWebhook}
                  required
                >
                  <option value="orders">Orders</option>
                  <option value="new_applications">New Applications</option>
                  <option value="application_status">Application Status Change</option>
                </select>
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
                  {editingWebhook ? 'Type cannot be changed after creation' : 'Select when this webhook should be triggered'}
                </small>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Webhook URL</label>
                <input
                  type="url"
                  className="admin-form-input"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  placeholder="https://discord.com/api/webhooks/..."
                  required
                  disabled={submitting}
                />
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
                  Discord webhook URL where notifications will be sent
                </small>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    name="active"
                    checked={formData.active}
                    onChange={handleChange}
                    disabled={submitting}
                  />
                  Active
                </label>
                <small style={{ color: 'var(--text-secondary)', display: 'block', marginTop: '0.25rem' }}>
                  Only active webhooks will receive notifications
                </small>
              </div>
              <div className="admin-form-group">
                <label className="admin-form-label">Discord Mentions</label>
                <div style={{ marginBottom: '0.5rem' }}>
                  <small style={{ color: 'var(--text-secondary)', display: 'block', marginBottom: '0.5rem' }}>
                    Add Discord user or role IDs to mention when this webhook is sent. Enter numeric IDs only (e.g., 123456789012345678).
                  </small>
                  {formData.mentions && formData.mentions.length > 0 && (
                    <div style={{ marginBottom: '0.5rem' }}>
                      {formData.mentions.map((mention, index) => {
                        const mentionObj = typeof mention === 'string' ? { type: 'user', id: mention } : mention;
                        return (
                          <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem',
                            marginBottom: '0.5rem',
                            padding: '0.5rem',
                            background: 'var(--bg-secondary, #1a1a1a)',
                            borderRadius: '4px'
                          }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              background: mentionObj.type === 'role' ? 'var(--accent-color, #ff6b6b)' : 'var(--bg-tertiary, #2a2a2a)',
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              textTransform: 'uppercase',
                              color: 'var(--text-primary)'
                            }}>
                              {mentionObj.type}
                            </span>

                            <span style={{ flex: 1, color: 'var(--text-primary)' }}>{mentionObj.id}</span>

                            <button
                              type="button"
                              className="admin-icon-button danger"
                              onClick={() => {
                                const newMentions = [...formData.mentions];
                                newMentions.splice(index, 1);
                                setFormData({ ...formData, mentions: newMentions });
                              }}
                              disabled={submitting}
                              title="Remove"
                            >
                              ×
                            </button>

                          </div>

                        );
                      })}
                    </div>

                  )}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <select
                      className="admin-form-select"
                      id="new-mention-type"
                      defaultValue="user"
                      style={{ width: '100px' }}
                      disabled={submitting}
                    >
                      <option value="user">User</option>

                      <option value="role">Role</option>

                    </select>

                    <input
                      type="text"
                      className="admin-form-input"
                      placeholder="Enter Discord ID (numeric)"
                      id="new-mention-input"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const input = e.target;
                          const typeSelect = document.getElementById('new-mention-type');
                          const value = input.value.trim();
                          if (value && /^\d+$/.test(value)) {
                            const mentionObj = { type: typeSelect.value, id: value };
                            const newMentions = [...(formData.mentions || []), mentionObj];
                            setFormData({ ...formData, mentions: newMentions });
                            input.value = '';
                          }
                        }
                      }}
                      disabled={submitting}
                      style={{ flex: 1 }}
                    />

                    <button
                      type="button"
                      className="admin-button"
                      onClick={() => {
                        const input = document.getElementById('new-mention-input');
                        const typeSelect = document.getElementById('new-mention-type');
                        const value = input.value.trim();
                        if (value && /^\d+$/.test(value)) {
                          const mentionObj = { type: typeSelect.value, id: value };
                          const newMentions = [...(formData.mentions || []), mentionObj];
                          setFormData({ ...formData, mentions: newMentions });
                          input.value = '';
                        } else {
                          alert('Please enter a valid numeric Discord ID');
                        }
                      }}
                      disabled={submitting}
                    >
                      Add
                    </button>

                  </div>

                </div>

              </div>


              <div className="admin-form-actions">
                <button
                  type="button"
                  className="admin-button secondary"
                  onClick={handleFormClose}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="admin-button primary"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : editingWebhook ? 'Update' : 'Create'}
                </button>

              </div>

            </form>

          </div>

        </div>

      )}
    </div>

  );
};

export default WebhooksManagement;

